import { useEffect, useMemo, useRef, useState } from 'react'
import GShell from './GShell.jsx'
import { Title, Card, Pill, Modal, Field, Err, money } from './gui.jsx'
import {
  watchProducts, createProduct, updateProduct, receiveStock, adjustStock, setCount,
  duplicateProduct, deleteProduct, isLow,
} from '../../lib/products.js'

const CSV_COLS = ['sku', 'barcode', 'name', 'brand', 'category', 'department', 'size', 'color', 'cost', 'retail', 'quantity', 'minQuantity', 'reorderQuantity']

export default function GInventory() {
  const [raw, setRaw] = useState({})
  const [q, setQ] = useState('')
  const [lowOnly, setLowOnly] = useState(false)
  const [manage, setManage] = useState(null)
  const [adding, setAdding] = useState(false)
  const [labelFor, setLabelFor] = useState(null)
  const fileRef = useRef(null)
  useEffect(() => { const off = watchProducts(setRaw); return () => off() }, [])

  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (a.name || a.sku || '').localeCompare(b.name || b.sku || '')), [raw])
  const lowCount = rows.filter(isLow).length
  const list = useMemo(() => {
    let r = rows
    if (lowOnly) r = r.filter(isLow)
    const s = q.trim().toLowerCase()
    if (s) r = r.filter((p) => `${p.name} ${p.sku} ${p.barcode} ${p.brand} ${p.category}`.toLowerCase().includes(s))
    return r
  }, [rows, q, lowOnly])

  const exportCsv = () => {
    const head = CSV_COLS.join(',')
    const body = rows.map((p) => CSV_COLS.map((c) => `"${String(p[c] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([head + '\n' + body], { type: 'text/csv' }))
    const a = document.createElement('a'); a.href = url; a.download = 'products.csv'; a.click(); URL.revokeObjectURL(url)
  }
  const importCsv = async (file) => {
    const text = await file.text()
    const lines = text.split(/\r?\n/).filter(Boolean)
    const cols = lines.shift().split(',').map((c) => c.trim())
    let n = 0
    for (const line of lines) {
      const vals = line.match(/("([^"]|"")*"|[^,]*)/g).filter((_, i, arr) => i < arr.length).slice(0, cols.length).map((v) => v.replace(/^"|"$/g, '').replace(/""/g, '"'))
      const rec = {}; cols.forEach((c, i) => rec[c] = vals[i]); if (rec.sku || rec.name) { await createProduct(rec); n++ }
    }
    alert(`Imported ${n} products.`)
  }

  return (
    <GShell>
      <section>
        <Title title="Inventory / Products" subtitle="Catalog, stock, pricing, labels & counts." right={
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary text-xs" onClick={exportCsv}>⬇ Export CSV</button>
            <button className="btn-secondary text-xs" onClick={() => fileRef.current?.click()}>⬆ Import CSV</button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && importCsv(e.target.files[0])} />
            <button className="btn-primary text-xs" onClick={() => setAdding(true)}>+ Add Product</button>
          </div>
        } />
      </section>

      {lowCount > 0 ? (
        <button onClick={() => setLowOnly((v) => !v)} className="text-left">
          <Card style={{ borderColor: '#F9731655', background: '#FFF7ED' }}>
            <span className="text-sm font-bold" style={{ color: '#C2410C' }}>⚠ {lowCount} product{lowCount === 1 ? '' : 's'} at/below min stock</span>
            <span className="text-xs text-gray-500 ml-2">{lowOnly ? '(showing low only — click to clear)' : '(click to filter)'}</span>
          </Card>
        </button>
      ) : null}

      <input className="input w-full max-w-sm" placeholder="Search name, SKU, barcode, brand…" value={q} onChange={(e) => setQ(e.target.value)} />

      <section className="pb-4">
        {list.length === 0 ? <p className="text-sm text-gray-500">{rows.length === 0 ? 'No products yet — add one.' : 'No matches.'}</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((p) => (
              <Card key={p.id} style={isLow(p) ? { borderColor: '#F9731655' } : {}}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="font-extrabold truncate" style={{ color: 'var(--ge-primary)' }}>{p.name || p.sku}</div>
                    <div className="text-xs text-gray-500 truncate">{[p.brand, p.category, p.size, p.color].filter(Boolean).join(' · ')}</div></div>
                  {isLow(p) ? <Pill bg="rgba(249,115,22,0.16)" fg="#C2410C">Low</Pill> : null}
                </div>
                <div className="flex items-baseline justify-between mt-2">
                  <div className="text-2xl font-extrabold" style={{ color: 'var(--ge-primary)' }}>{p.quantity ?? 0}<span className="text-xs text-gray-400 font-normal"> in stock</span></div>
                  <div className="text-right text-xs text-gray-500">{money(p.retail)}<div>{p.margin}% margin</div></div>
                </div>
                <div className="text-[11px] text-gray-400 mt-1">{p.sku ? `SKU ${p.sku}` : ''}{p.barcode ? ` · ${p.barcode}` : ''}{p.minQuantity ? ` · min ${p.minQuantity}` : ''}</div>
                <div className="mt-2 flex flex-wrap gap-1 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                  <button className="btn-secondary text-[11px]" onClick={() => setManage(p)}>Manage</button>
                  <button className="btn-ghost text-[11px]" onClick={() => setLabelFor(p)}>Label</button>
                  <button className="btn-ghost text-[11px]" onClick={async () => { await duplicateProduct(p.id, { sku: `${p.sku}-copy`, size: '', color: '' }, p) }}>Duplicate</button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {adding ? <ProductModal onClose={() => setAdding(false)} /> : null}
      {manage ? <ManageModal p={manage} onClose={() => setManage(null)} /> : null}
      {labelFor ? <LabelModal p={labelFor} onClose={() => setLabelFor(null)} /> : null}
    </GShell>
  )
}

const PFIELDS = [['name', 'Name'], ['sku', 'SKU'], ['barcode', 'Barcode'], ['brand', 'Brand'], ['category', 'Category'], ['department', 'Department'], ['size', 'Size'], ['color', 'Color'], ['cost', 'Cost $', 'number'], ['retail', 'Retail $', 'number'], ['quantity', 'Quantity', 'number'], ['minQuantity', 'Min qty', 'number'], ['reorderQuantity', 'Reorder qty', 'number']]

function ProductModal({ p, onClose }) {
  const [f, setF] = useState(p ? { ...p } : Object.fromEntries(PFIELDS.map(([k]) => [k, ''])))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const save = async () => { setBusy(true); setErr(''); try { p ? await updateProduct(p.id, f, p) : await createProduct(f); onClose() } catch (e) { setErr(e?.message || 'Failed'); setBusy(false) } }
  return (
    <Modal title={p ? `Edit — ${p.name || p.sku}` : 'Add Product'} onClose={onClose} wide>
      <div className="grid sm:grid-cols-3 gap-2">
        {PFIELDS.map(([k, ph, t]) => <Field key={k} label={ph}><input className="input w-full" type={t || 'text'} value={f[k] ?? ''} onChange={set(k)} /></Field>)}
      </div>
      <Field label="Image URLs (comma-separated)"><input className="input w-full" value={f.images || ''} onChange={set('images')} /></Field>
      <Field label="Notes"><input className="input w-full" value={f.notes || ''} onChange={set('notes')} /></Field>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></div>
      <Err msg={err} />
    </Modal>
  )
}

function ManageModal({ p, onClose }) {
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState(false)
  const hist = Object.values(p.history || {}).sort((a, b) => (b.ts || 0) - (a.ts || 0))
  if (editing) return <ProductModal p={p} onClose={() => setEditing(false)} />
  return (
    <Modal title={`Manage — ${p.name || p.sku}`} onClose={onClose} wide>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-3xl font-extrabold" style={{ color: 'var(--ge-primary)' }}>{p.quantity ?? 0} <span className="text-sm text-gray-400 font-normal">in stock</span></div>
        <button className="btn-secondary text-xs" onClick={() => setEditing(true)}>Edit details / price</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 items-end">
        <Field label="Quantity"><input className="input w-full" type="number" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
        <Field label="Note"><input className="input w-full" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <button className="btn-primary text-xs" onClick={async () => { await receiveStock(p.id, qty || 0, note || 'Received', p.quantity); setQty(''); setNote('') }}>Receive (+)</button>
        <button className="btn-secondary text-xs" onClick={async () => { await adjustStock(p.id, qty || 0, note || 'Adjust', p.quantity); setQty(''); setNote('') }}>Adjust (±)</button>
        <button className="btn-secondary text-xs" onClick={async () => { await setCount(p.id, qty || 0, note || 'Count', p.quantity); setQty(''); setNote('') }}>Set count (=)</button>
        <button className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm(`Delete ${p.name || p.sku}?`)) { await deleteProduct(p.id); onClose() } }}>Delete product</button>
      </div>
      <div className="mt-4">
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">History</div>
        <div className="max-h-48 overflow-y-auto text-xs space-y-1">
          {hist.length === 0 ? <p className="text-gray-400">No history.</p> : hist.map((h, i) => (
            <div key={i} className="flex justify-between gap-2 border-b py-0.5" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
              <span>{h.type}{h.delta != null ? ` (${h.delta > 0 ? '+' : ''}${h.delta})` : ''} — {h.note}</span>
              <span className="text-gray-400 shrink-0">{new Date(h.ts).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}

// Simple, printable price/barcode label. A real Code128 font can be dropped in
// later — the bars here are a decorative render of the barcode value.
function LabelModal({ p, onClose }) {
  const code = p.barcode || p.sku || p.id.slice(-8)
  const bars = code.split('').map((ch) => (ch.charCodeAt(0) % 4) + 1)
  const print = () => {
    const w = window.open('', '_blank', 'width=400,height=300')
    w.document.write(`<div style="font-family:sans-serif;text-align:center;padding:12px;border:1px solid #000;width:260px">
      <div style="font-weight:800">${p.brand || ''} ${p.name || p.sku}</div>
      <div style="font-size:28px;font-weight:800">$${(Number(p.retail) || 0).toFixed(2)}</div>
      <div style="font-family:monospace;letter-spacing:1px;font-size:20px">${'▐'.repeat(3)}|||${'▐'.repeat(3)}</div>
      <div style="font-family:monospace">${code}</div></div>`)
    w.document.close(); w.focus(); w.print()
  }
  return (
    <Modal title="Price / Barcode label" onClose={onClose}>
      <div className="mx-auto rounded-lg p-4 text-center" style={{ border: '1px solid #000', width: 260, background: '#fff' }}>
        <div className="font-extrabold">{p.brand} {p.name || p.sku}</div>
        <div className="text-3xl font-extrabold">{money(p.retail)}</div>
        <div className="flex items-end justify-center gap-[2px] h-8 my-1">{bars.map((h, i) => <span key={i} style={{ width: 2, height: `${h * 6}px`, background: '#111' }} />)}</div>
        <div className="font-mono text-sm">{code}</div>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Bars are decorative — drop in a Code128 font/lib later for scannable labels.</p>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-secondary text-sm" onClick={print}>🖨 Print label</button><button className="btn-ghost text-sm" onClick={onClose}>Close</button></div>
    </Modal>
  )
}
